// Copyright © 2025 CATTLEytics Inc.

import { formatInTimeZone } from 'date-fns-tz';
import { Spinner } from 'react-bootstrap';

import { isNullOrWhitespace } from '../../shared';
import { ReportSection } from './ReportSection';
import { useSourceReport } from './useSourceReport';

interface PivotQueryProps {
  children?: (props: { data: any[] }) => JSX.Element;
  reportName: string;
}

export function PivotQuery({ reportName, children }: PivotQueryProps): JSX.Element {
  const query = useSourceReport(reportName);

  if (query.isLoading) {
    return (
      <ReportSection title={reportName}>
        <Spinner />
      </ReportSection>
    );
  }

  if (query.isError) {
    return <ReportSection title={reportName}>Error loading data</ReportSection>;
  }

  if (!Array.isArray(query.data) || query.data.length === 0) {
    return <ReportSection title={reportName}>No data available</ReportSection>;
  }

  // pre-process data
  const processedData = query.data.map((row) => {
    // Convert all values to strings for consistency
    const formattedColumns: Record<string, string> = {};

    if (!isNullOrWhitespace(row['month'])) {
      formattedColumns['month'] = formatInTimeZone(row['month'], 'UTC', 'yyyy-MM-dd');
    }

    return { ...row, ...formattedColumns };
  });

  return (
    <ReportSection title={reportName}>
      {children ? children({ data: processedData }) : null}
    </ReportSection>
  );
}
